import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string[];
  image?: string;
  type?: 'website' | 'article';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
}

/**
 * Normalise a pathname to the canonical form used by the site:
 *  - strip trailing slash (except for "/")
 *  - always lowercase
 */
const normalisePath = (path: string): string => {
  let p = path.toLowerCase();
  if (p.length > 1 && p.endsWith('/')) {
    p = p.slice(0, -1);
  }
  return p;
};

export const SEOHead = ({
  title,
  description,
  keywords = [],
  image = '/og-affarsnatverket.png',
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  noindex = false,
}: SEOHeadProps) => {
  const location = useLocation();

  // Canonical: always https://affarsnatverket.se, no query params, no trailing slash
  const canonicalUrl = `https://affarsnatverket.se${normalisePath(location.pathname)}`;
  const fullTitle = title.includes('Affärsnätverket') ? title : `${title} | Affärsnätverket`;
  const imageUrl = /^https?:\/\//i.test(image) ? image : `https://affarsnatverket.se${image}`;

  useEffect(() => {
    document.title = fullTitle;

    const setMetaTag = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Basic meta tags
    setMetaTag('description', description);
    if (keywords.length > 0) {
      setMetaTag('keywords', keywords.join(', '));
    }

    // Open Graph tags
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:image', imageUrl, true);
    setMetaTag('og:site_name', 'Affärsnätverket', true);
    setMetaTag('og:locale', 'sv_SE', true);

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', imageUrl);

    // Article specific tags
    if (type === 'article') {
      if (author) setMetaTag('article:author', author, true);
      if (publishedTime) setMetaTag('article:published_time', publishedTime, true);
      if (modifiedTime) setMetaTag('article:modified_time', modifiedTime, true);
    }

    // Canonical URL — always self-referencing, no params
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Robots meta
    if (noindex) {
      setMetaTag('robots', 'noindex, nofollow');
      setMetaTag('googlebot', 'noindex, nofollow');
    } else {
      setMetaTag('robots', 'index, follow');
      setMetaTag('googlebot', 'index, follow');
    }
  }, [fullTitle, description, keywords, imageUrl, type, author, publishedTime, modifiedTime, canonicalUrl, noindex]);

  return null;
};
